import { getSpaceName } from "@app/lib/spaces";
import type { SpaceCategoryInfo } from "@app/types/api/spaces";
import type { SpaceType } from "@app/types/space";
import {
  Button,
  ContentMessage,
  Dialog,
  DialogContainer,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Spinner,
  Trash01,
} from "@dust-tt/sparkle";

interface ConfirmDeleteSpaceDialogProps {
  space: SpaceType;
  handleDelete: () => void;
  spaceInfoByCategory: { [key: string]: SpaceCategoryInfo } | undefined;
  isDeleting: boolean;
}

export function ConfirmDeleteSpaceDialog({
  space,
  handleDelete,
  spaceInfoByCategory,
  isDeleting,
}: ConfirmDeleteSpaceDialogProps) {
  const uniqueAgentNames = spaceInfoByCategory
    ? [
        ...new Set(
          Object.values(spaceInfoByCategory)
            .flatMap((category) => category.usage.agents)
            .map((agent) => agent.name)
            .filter((name) => name && name.length > 0)
        ),
      ]
    : [];
  const uniqueSkillNames = spaceInfoByCategory
    ? [
        ...new Set(
          Object.values(spaceInfoByCategory)
            .flatMap((category) => category.usage.skills)
            .map((skill) => skill.name)
            .filter((name) => name && name.length > 0)
        ),
      ]
    : [];

  const spaceName = `${getSpaceName(space)}`;
  const hasAgents = uniqueAgentNames.length > 0;
  const hasSkills = uniqueSkillNames.length > 0;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div className="flex w-full flex-col items-end">
          <Button
            icon={Trash01}
            size="xs"
            variant="warning"
            label="Delete space"
          />
        </div>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`Deleting ${getSpaceName(space)}`}</DialogTitle>
        </DialogHeader>
        {isDeleting ? (
          <div className="flex justify-center py-8">
            <Spinner variant="dark" size="md" />
          </div>
        ) : (
          <>
            <DialogContainer className="space-y-4">
              {hasAgents && (
                <ContentMessage
                  variant="warning"
                  // TODO: change to show names of public agents and then number of unpublished agents
                  title={`${uniqueAgentNames.length} agent${uniqueAgentNames.length === 1 ? "" : "s"}
                    use${uniqueAgentNames.length === 1 ? "s" : ""} tools that depend on this space
                    and will be impacted by its deletion`}
                />
              )}
              {hasSkills && (
                <ContentMessage
                  variant="warning"
                  title={`${uniqueSkillNames.length} skill${uniqueSkillNames.length === 1 ? "" : "s"}
                    depend${uniqueSkillNames.length === 1 ? "s" : ""} on this space
                    and will be impacted by its deletion`}
                />
              )}
              <div>
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to permanently delete space {spaceName}?
                  This action cannot be undone.
                </p>
              </div>
            </DialogContainer>
            <DialogFooter
              leftButtonProps={{
                label: "Cancel",
                variant: "outline",
              }}
              rightButtonProps={{
                label: "Delete",
                variant: "warning",
                onClick: async () => {
                  void handleDelete();
                },
              }}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
